/**
 * Content text: a locale record `{ en, …other locales }` (English is the required source). Content files author
 * English + Vietnamese inline with `t(en, vi)`; every other locale comes from the per-locale content catalogs
 * (i18n/catalog.ts, keyed by the English text) or may be given inline. `localize()` walks the locale's fallback chain
 * (zh-Hant → zh-Hans → en, pt-BR → en). `npm run i18n:check` + the content tests block untranslated content for every
 * available locale. UI chrome strings live in apps/client/src/lib/i18n. Locale registry: i18n/locales.ts.
 */
import { AVAILABLE_LOCALES, fallbackChain, joinList, type Locale } from './i18n/locales';
import { catalogLookup } from './i18n/catalog';

/** App language = a registry locale (only AVAILABLE_LOCALES are selectable). */
export type Lang = Locale;
/** Selectable (shipped) languages. */
export const LANGS: Lang[] = AVAILABLE_LOCALES;

/** Values a template text can insert: another text, a list of texts (joined "a, b and c" per locale), a string / number. */
export type TemplateVar = L | L[] | string | number;
/** Source of a composed text (`tf`): the English template + its variables, so other locales translate the template once. */
export interface TemplateSource {
  en: string;
  vars: Record<string, TemplateVar>;
}

export type L = { en: string } & { [K in Exclude<Locale, 'en'>]?: string } & {
  /** Set by `tf()`: catalog locales translate `$t.en` (with `{var}` placeholders) instead of every composed sentence. */
  $t?: TemplateSource;
};

/** Tooling hook (tools/i18n): when set before the content modules load, sees every text created with `t()`. */
type TextCollector = (text: L) => void;
const collector = (): TextCollector | undefined => (globalThis as { __skI18nCollect?: TextCollector }).__skI18nCollect;

/** Shorthand constructor used by content files: t('Draw a circle', 'Vẽ một hình tròn'). */
export const t = (en: string, vi: string): L => {
  const o: L = { en, vi };
  collector()?.(o);
  return o;
};

/** Fill `{var}` placeholders of a template with localised variables. */
export function fillTemplate(template: string, vars: Record<string, TemplateVar>, lang: Lang): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => {
    if (!(k in vars)) return m;
    const v = vars[k]!;
    if (Array.isArray(v)) return joinList(v.map((x) => localize(x, lang)), lang);
    return typeof v === 'object' ? localize(v, lang) : String(v);
  });
}

/**
 * Composed content text: `tf('Colour the *{part}*', 'Tô màu *{part}*', { part: label })`. English / Vietnamese are filled
 * now (inline values, exactly as `t()` would give); catalog locales translate the template once and fill it with
 * their own variable values at `localize()` time.
 */
export const tf = (en: string, vi: string, vars: Record<string, TemplateVar>): L => {
  const o: L = { en: fillTemplate(en, vars, 'en'), vi: fillTemplate(vi, vars, 'vi'), $t: { en, vars } };
  collector()?.(o);
  return o;
};

const chains = new Map<Locale, Locale[]>();
const chainOf = (l: Locale) => {
  let c = chains.get(l);
  if (!c) chains.set(l, (c = fallbackChain(l)));
  return c;
};

/** The text in `lang`: inline value, else the content catalog, along the fallback chain; English last. */
export function localize(text: L | string, lang: Lang): string {
  if (typeof text === 'string') return text;
  for (const l of chainOf(lang)) {
    if (text[l]) return text[l];
    if (l === 'en') break;
    const tpl = text.$t && catalogLookup(l, text.$t.en);
    if (tpl) return fillTemplate(tpl, text.$t!.vars, l);
    const v = catalogLookup(l, text.en);
    if (v) return v;
  }
  return text.en;
}

/** True when `lang` has its own value for the text (inline or catalog), without falling back. */
export function hasLocale(text: L, lang: Lang): boolean {
  return lang === 'en' ? !!text.en : !!(text[lang] || (text.$t && catalogLookup(lang, text.$t.en)) || catalogLookup(lang, text.en));
}

/**
 * Fill `{name}`-style placeholders. Unknown keys are left as-is so a typo is visible in QA.
 */
export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/**
 * Rich lesson copy: `*word*` marks an emphasised word, optionally with a crayon token:
 * `*inner circle*{highlight}`. Tokens: highlight | game | success | brand | coral.
 */
export type EmphasisToken = 'highlight' | 'game' | 'success' | 'brand' | 'coral';
export const EMPHASIS_TOKENS: EmphasisToken[] = ['highlight', 'game', 'success', 'brand', 'coral'];
export interface RichSpan {
  text: string;
  token?: EmphasisToken;
}

export function parseRich(s: string): RichSpan[] {
  const out: RichSpan[] = [];
  const re = /\*([^*]+)\*(?:\{(\w+)\})?/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) {
    if (m.index > last) out.push({ text: s.slice(last, m.index) });
    const tok = (m[2] ?? 'highlight') as EmphasisToken;
    out.push({ text: m[1]!, token: EMPHASIS_TOKENS.includes(tok) ? tok : 'highlight' });
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push({ text: s.slice(last) });
  return out;
}

/** Plain text (for TTS) with the emphasis markup removed. */
export const plainText = (s: string) => parseRich(s).map((p) => p.text).join('');
